<script setup>
import { computed } from "vue";
import { cn } from "@/shared/lib/utils";
import { SvgIcon } from "@/shared/ui/svg-icon";

const props = defineProps({
  class: { type: String, default: "" },
  icon: { type: String, default: "" },
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  card: { type: Boolean, default: false },
});

const rootClass = computed(() =>
  cn(
    props.card
      ? "empty-card"
      : "flex min-h-[200px] flex-col items-center justify-center gap-6 text-center",
    props.class
  )
);
</script>

<template>
  <div :class="rootClass">
    <div v-if="card" class="empty-deco" />

    <template v-if="card">
      <div class="empty-content">
        <div v-if="$slots.icon || icon" class="empty-icon">
          <slot name="icon">
            <SvgIcon v-if="icon" :name="icon" />
          </slot>
        </div>

        <div v-if="title" class="empty-title">{{ title }}</div>

        <div v-if="description || $slots.default" class="empty-text">
          <slot>{{ description }}</slot>
        </div>
      </div>

      <div v-if="$slots.action" class="empty-action">
        <slot name="action" />
      </div>
    </template>

    <template v-else>
      <div v-if="$slots.icon || icon" class="text-muted-foreground">
        <slot name="icon">
          <SvgIcon v-if="icon" :name="icon" />
        </slot>
      </div>

      <div v-if="title" class="text-base font-semibold text-foreground">
        {{ title }}
      </div>

      <div
        v-if="description || $slots.default"
        class="text-sm text-muted-foreground"
      >
        <slot>{{ description }}</slot>
      </div>

      <div v-if="$slots.action">
        <slot name="action" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.empty-card {
  @apply flex-1 flex flex-col items-center justify-center text-center gap-4
    py-6 relative;
  min-height: 280px;
}

.empty-deco {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.empty-card .empty-content {
  @apply flex flex-col items-center gap-4 relative z-[1] max-w-md;
}

.empty-card .empty-icon {
  @apply w-14 h-14 rounded-[var(--radius)] bg-muted/40 border border-border
    flex items-center justify-center text-muted-foreground mb-1;

  :deep(svg) {
    @apply w-6 h-6;
  }
}

.empty-card .empty-title {
  @apply text-foreground text-base font-semibold;
}

.empty-card .empty-text {
  @apply text-sm text-muted-foreground leading-[1.55] max-w-md;
}

.empty-card .empty-action {
  @apply relative z-[1] w-full max-w-md mx-auto;
}
</style>
